/**
 * Meeting Minutes — PDF export.
 *
 * Same engine as the AI Use Case Register (`pdfDoc.ts`): measured, programmatic layout with
 * real text and clean page breaks, not a screenshot. This module is only the minutes-specific
 * composition on top of it.
 *
 * Layout decision worth stating: entries are printed **grouped by category**, not in author
 * order, and the groups run Decision → Action → Risk → Blocker → Dependency → Information.
 * Minutes are read afterwards to answer two questions — "what did we decide?" and "what am I
 * on the hook for?" — so those two groups come first. Everything else is context and follows.
 * The on-screen register keeps author order; only the printed artefact regroups.
 */
import type { MeetingMinutes, MinuteCategory, MinuteEntry, MinuteEntryStatus } from '../types/models';
import { MINUTES_STATUS_LABELS, MINUTES_STATUS_TONE } from '../constants/meetingMinutes';
import { formatDate } from './format';
import { Doc, T, htmlToParagraphs, stripHtml, type Cell, type PdfBuildResult } from './pdfDoc';

const FOOTER_LABEL = 'Mercell Nimbus — Meeting Minutes';

/** Reading order of the category groups. See the module comment for why. */
const CATEGORY_PRINT_ORDER: MinuteCategory[] = [
  'decision', 'action', 'risk', 'blocker', 'dependency', 'information',
];

/** Group headings are plural; the shared constants are singular because they label one chip. */
const CATEGORY_GROUP_LABELS: Record<MinuteCategory, string> = {
  decision:    'Decisions',
  action:      'Actions',
  risk:        'Risks',
  blocker:     'Blockers',
  dependency:  'Dependencies',
  information: 'Information',
};

/** Column header for the body column of a single-column group table. */
const CATEGORY_COLUMN_LABELS: Record<MinuteCategory, string> = {
  decision:    'Decision',
  action:      'Action',
  risk:        'Risk',
  blocker:     'Blocker',
  dependency:  'Dependency',
  information: 'Note',
};

// Entry open/done has no shared label map — it exists only inside an action row, on screen
// as a switch and here as a chip. Kept local rather than editing the shared constants file.
const ENTRY_STATUS_LABELS: Record<MinuteEntryStatus, string> = { open: 'Open', done: 'Done' };
const ENTRY_STATUS_TONE: Record<MinuteEntryStatus, string> = { open: 'warning', done: 'success' };

/** Enough of a person to print. `peopleApi.getAll()` rows satisfy this, as does `Person`. */
export interface PdfPerson {
  personId: string;
  personName: string;
}

/** Enough of an initiative to print. `initiativesApi.getAll()` rows satisfy this. Minutes
 *  store only the initiativeId; the tracker owns the display name, so the caller passes
 *  the lookup in rather than this module reaching for another API. */
export interface PdfInitiative {
  initiativeId: string;
  name: string;
}

/** One item carried forward from an earlier meeting on the same initiative, as shown in
 *  the detail page's carry-forward panel and optionally bundled into the annex below. */
export interface AnnexEntry {
  source: MeetingMinutes;
  entry: MinuteEntry;
}

/** Which categories to print as an annex, and the carried items for each. Actions are
 *  never annex material — see the caller's own comment on ANNEX_CATEGORIES for why. */
export type AnnexSelection = Partial<Record<MinuteCategory, AnnexEntry[]>>;

// ─── Value formatting ─────────────────────────────────────────────────────────

/** 'HH:mm–HH:mm', or a one-sided range, or nothing at all. */
const timeRange = (start: string, end: string): string => {
  if (start && end) return `${start}–${end}`;
  if (start) return `from ${start}`;
  if (end) return `until ${end}`;
  return '';
};

const nameResolver = (people: PdfPerson[]) => {
  const byId = new Map(people.map(p => [p.personId, p.personName]));
  /** Falls back to the raw id: a person removed from the register is still better shown
   *  as 'PER-004' than silently dropped from an attendance list. */
  return (id: string): string => byId.get(id) || id;
};

/** Same contract as {@link nameResolver}, for the initiative a meeting is pinned to. */
const initiativeResolver = (initiatives: PdfInitiative[]) => {
  const byId = new Map(initiatives.map(i => [i.initiativeId, i.name]));
  return (id: string): string => byId.get(id) || id;
};

const timestamp = (iso: string): string =>
  iso ? new Date(iso).toLocaleString('en-GB') : '—';

/**
 * Cross-references that would otherwise be invisible in print: carried-forward provenance,
 * linked requirements and milestones, and promotion to a risk or issue. Appended to the entry
 * body as a bracketed trailer so a single-column table still carries the whole record.
 */
const entryTrailer = (e: MinuteEntry): string => {
  const bits: string[] = [];
  if (e.carriedFromMinutesId) bits.push(`carried from ${e.carriedFromMinutesId}`);
  const links = [...(e.requirementIds ?? []), ...(e.milestoneIds ?? [])];
  if (links.length) bits.push(`linked ${links.join(', ')}`);
  if (e.promotedArtefactId) {
    bits.push(`promoted to ${e.promotedArtefactType || 'artefact'} ${e.promotedArtefactId}`);
  }
  return bits.length ? `  [${bits.join(' · ')}]` : '';
};

const entryBody = (e: MinuteEntry): string =>
  (stripHtml(e.body).trim() || '—') + entryTrailer(e);

// ─── Renderers ────────────────────────────────────────────────────────────────

function renderAttendance(doc: Doc, m: MeetingMinutes, personName: (id: string) => string): void {
  const attendees = (m.attendeeIds ?? []).map(personName);
  const apologies = (m.apologyIds ?? []).map(personName);
  const external = (m.externalAttendees ?? '').trim();

  doc.sectionHeading('Attendance');
  doc.metaRow('Attendees', attendees.length ? attendees.join(', ') : 'None recorded');
  if (external) doc.metaRow('External attendees', external);
  doc.metaRow('Apologies', apologies.length ? apologies.join(', ') : 'None');
  doc.space(1);
}

/** Actions carry an owner, a due date and an open/done state; nothing else does. */
function renderActionsTable(doc: Doc, entries: MinuteEntry[], personName: (id: string) => string): void {
  const rows: Cell[][] = entries.map(e => [
    entryBody(e),
    e.ownerId ? personName(e.ownerId) : 'Unassigned',
    formatDate(e.dueDate),
    { text: ENTRY_STATUS_LABELS[e.status] ?? e.status, tone: ENTRY_STATUS_TONE[e.status] ?? 'neutral' },
  ]);
  doc.table(['Action', 'Owner', 'Due', 'Status'], [92, 42, 22, 22], rows);
}

function renderBodyTable(doc: Doc, category: MinuteCategory, entries: MinuteEntry[]): void {
  const rows: Cell[][] = entries.map(e => [entryBody(e)]);
  doc.table([CATEGORY_COLUMN_LABELS[category]], [178], rows);
}

/**
 * A table row cannot be split. `table()` measures each row and moves it whole to a fresh
 * page if it does not fit — but a row taller than an entire page has nowhere to move to,
 * and draws straight past the bottom margin. Measured with the harness in
 * `scripts/verify-meeting-minutes-pdf.mjs`, that happens at about 4,080 plain-text
 * characters in the 92mm Action column and about 8,145 in the 178mm body column.
 *
 * Real minute entries are a sentence or two, but a pasted wall of text must not silently
 * produce a cut PDF, so anything over budget is printed as flowing paragraphs instead —
 * `fieldParagraphs` reserves line by line and crosses page breaks safely. The budgets below
 * are half the measured figures, which covers text set entirely in the widest glyphs
 * helvetica has (a char budget is only a proxy for width).
 */
const ROW_BUDGET_CHARS = { action: 2000, body: 4000 } as const;

const fitsOneRow = (e: MinuteEntry, category: MinuteCategory): boolean =>
  entryBody(e).length <= (category === 'action' ? ROW_BUDGET_CHARS.action : ROW_BUDGET_CHARS.body);

/** One over-long entry, as a labelled block rather than a table row. */
function renderLongEntry(
  doc: Doc,
  category: MinuteCategory,
  e: MinuteEntry,
  personName: (id: string) => string,
): void {
  // Actions carry their owner, due date and state in the label, where the table would have
  // carried them in columns.
  const facts = category === 'action'
    ? [
      e.ownerId ? personName(e.ownerId) : 'Unassigned',
      e.dueDate ? `due ${formatDate(e.dueDate)}` : '',
      ENTRY_STATUS_LABELS[e.status] ?? e.status,
    ].filter(Boolean).join(' · ')
    : '';
  const label = `${CATEGORY_COLUMN_LABELS[category]}${facts ? ` — ${facts}` : ''}`;

  const paras = htmlToParagraphs(e.body);
  if (!paras.length) paras.push('—');
  // Trailer appended to the last paragraph, exactly as the table form appends it to the cell.
  paras[paras.length - 1] += entryTrailer(e);

  doc.fieldParagraphs(label, paras);
}

/**
 * One category group. Entries keep their author order: consecutive entries that fit a table
 * row are batched into a table, and an over-long one is emitted in place as a block, which
 * closes the current table and starts a new one after it.
 */
function renderGroup(
  doc: Doc,
  category: MinuteCategory,
  items: MinuteEntry[],
  personName: (id: string) => string,
): void {
  let batch: MinuteEntry[] = [];
  const flush = (): void => {
    if (!batch.length) return;
    if (category === 'action') renderActionsTable(doc, batch, personName);
    else renderBodyTable(doc, category, batch);
    batch = [];
  };

  items.forEach(e => {
    if (fitsOneRow(e, category)) {
      batch.push(e);
      return;
    }
    flush();
    renderLongEntry(doc, category, e, personName);
  });
  flush();
}

function renderEntries(doc: Doc, m: MeetingMinutes, personName: (id: string) => string): void {
  const entries = m.entries ?? [];
  const groups = CATEGORY_PRINT_ORDER
    .map(category => ({ category, items: entries.filter(e => e.category === category) }))
    .filter(g => g.items.length > 0);

  if (!groups.length) {
    doc.sectionHeading('Minutes');
    doc.paragraph('No entries were recorded for this meeting.', T.small, { gap: 2 });
    return;
  }

  groups.forEach(({ category, items }) => {
    // 14mm of reserved follow-on: the table's own header band is 6mm and its first row is
    // about 6.5mm, so a group heading can never be left stranded above a page break. A
    // block-form entry needs less than that (a label plus two lines is ~12.6mm), so the
    // same reservation covers a group that opens with one.
    doc.sectionHeading(`${CATEGORY_GROUP_LABELS[category]} (${items.length})`, 14);
    renderGroup(doc, category, items, personName);
  });
}

function renderMinutes(
  doc: Doc,
  m: MeetingMinutes,
  personName: (id: string) => string,
  initiativeName: (id: string) => string,
): void {
  const chips: Array<{ text: string; tone: string }> = [
    {
      text: MINUTES_STATUS_LABELS[m.status] ?? m.status,
      tone: MINUTES_STATUS_TONE[m.status] ?? 'neutral',
    },
  ];
  if (m.initiativeId) chips.push({ text: m.initiativeId, tone: 'info' });

  doc.recordHeading(m.minutesId, m.title || m.minutesId, chips);

  // Cover line: when the meeting happened, and for how long if that was recorded. A
  // printed record always states the date, even if only to say it is missing.
  const when = [
    m.meetingDate ? formatDate(m.meetingDate) : 'No meeting date recorded',
    timeRange(m.startTime ?? '', m.endTime ?? ''),
  ].filter(Boolean).join(' · ');
  doc.paragraph(when, T.bodyStrong, { gap: m.initiativeId ? 1.5 : 3 });

  // The initiative carries its name here rather than in a third chip: names run long and
  // chip() neither wraps nor measures against the page edge, so a long one would print
  // off the side. metaRow wraps both columns. The id stays in the heading chip as the
  // stable cross-reference.
  if (m.initiativeId) {
    doc.metaRow('Initiative', initiativeName(m.initiativeId));
    doc.space(1);
  }

  renderAttendance(doc, m, personName);

  const agenda = htmlToParagraphs(m.agenda ?? '');
  if (agenda.length) {
    doc.sectionHeading('Agenda');
    doc.paragraphs(agenda);
  }

  renderEntries(doc, m, personName);

  doc.sectionHeading('Record');
  doc.metaRow('Status', MINUTES_STATUS_LABELS[m.status] ?? m.status);
  if (m.approvedBy || m.approvedAt) {
    const who = m.approvedBy ? personName(m.approvedBy) : '—';
    doc.metaRow('Approved', m.approvedAt ? `${who} · ${timestamp(m.approvedAt)}` : who);
  }
  doc.metaRow('Created', timestamp(m.createdAt));
  doc.metaRow('Last updated', timestamp(m.updatedAt));
}

/**
 * The annex: items carried forward from earlier meetings on this initiative, grouped by
 * category and printed with their source meeting — the one piece of context the main
 * body's own tables never need, since every row there already belongs to this meeting.
 * Actions are never passed in here (see ANNEX_CATEGORIES in the detail page).
 */
const fitsAnnexRow = (e: MinuteEntry): boolean => entryBody(e).length <= ROW_BUDGET_CHARS.body;

function renderAnnexLongEntry(doc: Doc, category: MinuteCategory, item: AnnexEntry): void {
  const { source, entry } = item;
  const label = `${CATEGORY_COLUMN_LABELS[category]} — raised in ${source.title || source.minutesId} · ${formatDate(source.meetingDate)}`;
  const paras = htmlToParagraphs(entry.body);
  if (!paras.length) paras.push('—');
  paras[paras.length - 1] += entryTrailer(entry);
  doc.fieldParagraphs(label, paras);
}

function renderAnnexGroup(doc: Doc, category: MinuteCategory, items: AnnexEntry[]): void {
  let batch: AnnexEntry[] = [];
  const flush = (): void => {
    if (!batch.length) return;
    const rows: Cell[][] = batch.map(({ source, entry }) => [
      entryBody(entry),
      source.title || source.minutesId,
      formatDate(source.meetingDate),
    ]);
    doc.table([CATEGORY_COLUMN_LABELS[category], 'Raised in', 'Date'], [98, 58, 22], rows);
    batch = [];
  };

  items.forEach((item) => {
    if (fitsAnnexRow(item.entry)) {
      batch.push(item);
      return;
    }
    flush();
    renderAnnexLongEntry(doc, category, item);
  });
  flush();
}

function renderAnnex(doc: Doc, annex: AnnexSelection): void {
  const groups = CATEGORY_PRINT_ORDER
    .map((category) => ({ category, items: annex[category] ?? [] }))
    .filter((g) => g.items.length > 0);
  if (!groups.length) return;

  doc.pageBreak();
  doc.sectionHeading('Annex — Carried Forward from Previous Meetings', 14);
  doc.paragraph(
    'Items raised in earlier meetings on this initiative, still outstanding as of this export.',
    T.small,
    { gap: 3 }
  );
  groups.forEach(({ category, items }) => {
    doc.sectionHeading(`${CATEGORY_GROUP_LABELS[category]} (${items.length})`, 14);
    renderAnnexGroup(doc, category, items);
  });
}

// ─── Public entry points ──────────────────────────────────────────────────────

export type { PdfBuildResult };

/**
 * Build the document without triggering a download, so the pagination guarantee can be
 * verified headlessly the same way the AI register's is.
 *
 * Accepts either one set of minutes (the detail page) or many (a filtered register export).
 * `people` and `initiatives` are the reference data needed to print names instead of ids;
 * both are optional and fall back to printing the id.
 */
export function buildMeetingMinutesPdf(
  minutes: MeetingMinutes | MeetingMinutes[],
  people: PdfPerson[] = [],
  initiatives: PdfInitiative[] = [],
  opts: { onePerPage?: boolean; now?: Date; annex?: AnnexSelection } = {},
): PdfBuildResult {
  const records = Array.isArray(minutes) ? minutes : [minutes];
  const personName = nameResolver(people);
  const initiativeName = initiativeResolver(initiatives);

  const generatedAt = (opts.now ?? new Date()).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const doc = new Doc(generatedAt, FOOTER_LABEL);
  const plural = records.length === 1 ? 'meeting' : 'meetings';
  doc.coverTitle('Meeting Minutes', `${records.length} ${plural} · generated ${generatedAt}`);

  records.forEach((m, i) => {
    if (i > 0) {
      if (opts.onePerPage !== false) doc.pageBreak();
      else doc.space(6);
    }
    renderMinutes(doc, m, personName, initiativeName);
  });

  if (opts.annex) renderAnnex(doc, opts.annex);

  const { pdf, pages, maxYByPage, overflows } = doc.finish();
  const filename = records.length === 1
    ? `${records[0].minutesId}-meeting-minutes.pdf`
    : `meeting-minutes-${records.length}-records.pdf`;

  return { pages, maxYByPage, overflows, filename, save: () => pdf.save(filename) };
}

/** Build and download. */
export function exportMeetingMinutesPdf(
  minutes: MeetingMinutes | MeetingMinutes[],
  people: PdfPerson[] = [],
  initiatives: PdfInitiative[] = [],
  opts: { onePerPage?: boolean; annex?: AnnexSelection } = {},
): void {
  buildMeetingMinutesPdf(minutes, people, initiatives, opts).save();
}
